import { FetchError, fetcher, type FetchParams } from '@commenda/utils';

import type { CommendaAiMutationResult } from './types';

/** Every agent API call goes through the shared `fetcher`, so a terminated session redirects to sign-in. */
export class AgentApiRequestHelper {
  /** Reads feed SWR and the transport, which expect a throw; the FetchError carries the status. */
  static async read<T>(path: string, signal?: AbortSignal): Promise<T> {
    const response = await fetcher(path, {
      method: 'GET',
      ...(signal ? { signal } : {}),
    });
    const body = await AgentApiRequestHelper.parseBody(response);

    if (!response.ok)
      throw new FetchError(
        AgentApiRequestHelper.getErrorMessage(
          body,
          `Request failed: ${response.status}`
        ),
        AgentApiRequestHelper.isRecord(body) ? body : {},
        response.status
      );

    // Safe by contract: the body is the backend's typed response for this route.
    return body as T;
  }

  static async mutate<T = undefined>(
    path: string,
    params: Readonly<FetchParams>,
    fallbackError: string
  ): Promise<CommendaAiMutationResult<T>> {
    try {
      const response = await fetcher(path, params);
      const body = await AgentApiRequestHelper.parseBody(response);

      if (!response.ok)
        return {
          success: false,
          error: AgentApiRequestHelper.getErrorMessage(body, fallbackError),
          status: response.status,
        };

      // Safe by contract: the body is the backend's typed response for this route.
      return { success: true, data: body as T };
    } catch (error: unknown) {
      return {
        success: false,
        error: error instanceof Error ? error.message : fallbackError,
      };
    }
  }

  /** Like `mutate`, but the success body is returned unread so it can be streamed. */
  static async stream(
    path: string,
    params: Readonly<FetchParams>,
    fallbackError: string
  ): Promise<CommendaAiMutationResult<ReadableStream<Uint8Array>>> {
    try {
      const response = await fetcher(path, params);

      if (!response.ok || !response.body) {
        const body = await AgentApiRequestHelper.parseBody(response);
        return {
          success: false,
          error: AgentApiRequestHelper.getErrorMessage(body, fallbackError),
          status: response.status,
        };
      }

      return { success: true, data: response.body };
    } catch (error: unknown) {
      return {
        success: false,
        error: error instanceof Error ? error.message : fallbackError,
      };
    }
  }

  /** DELETE answers 204 and stop an empty 200, so an empty body parses to undefined. */
  private static async parseBody(response: Response): Promise<unknown> {
    const text = await response.text();
    if (!text) return undefined;

    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  private static getErrorMessage(body: unknown, fallback: string): string {
    if (!AgentApiRequestHelper.isRecord(body)) return fallback;

    const { error, message } = body;
    if (AgentApiRequestHelper.isRecord(error)) {
      const { detail, title } = error;
      if (
        AgentApiRequestHelper.isRecord(detail) &&
        typeof detail.description === 'string'
      )
        return detail.description;
      if (typeof title === 'string') return title;
    }

    if (typeof message === 'string' && message) return message;
    if (Array.isArray(message) && message.length > 0)
      return message.map(String).join(', ');

    return fallback;
  }

  private static isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }
}
